import {SupportedLocale} from '@/initializers/i18next';
import {ValueResolver, ValueResolverOptions} from '../types';
import {normalizeDateInput} from '../utils/date';

export const TEMPLATE_EXPRESSION_REGEX = /(?<func>\w+)\((?<arg>[^)]+)\)/;
export const TEMPLATE_REGEX = /\${([^}]+)}/g;
export const TEMPLATE_CALC_REGEX = /(?<left>\w+(?::\w+)?)\s*(?<operator>[-+*/])\s*(?<right>\w+(?::\w+)?)/;

// Function to find template expressions with proper brace matching
const findTemplateExpressions = (
  template: string,
): Array<{match: string; expression: string; start: number; end: number}> => {
  const results: Array<{match: string; expression: string; start: number; end: number}> = [];
  let currentIndex = 0;

  while (currentIndex < template.length) {
    if (template.substring(currentIndex, currentIndex + 2) === '${') {
      let braceCount = 1;
      let templateEndIndex = currentIndex + 2;

      while (templateEndIndex < template.length && braceCount > 0) {
        if (template.substring(templateEndIndex, templateEndIndex + 2) === '${') {
          braceCount++;
          templateEndIndex += 2;
        } else if (template[templateEndIndex] === '}') {
          braceCount--;
          templateEndIndex++;
        } else {
          templateEndIndex++;
        }
      }

      if (braceCount === 0) {
        const match = template.substring(currentIndex, templateEndIndex);
        const expression = template.substring(currentIndex + 2, templateEndIndex - 1);
        results.push({match, expression, start: currentIndex, end: templateEndIndex});
        currentIndex = templateEndIndex;
      } else {
        currentIndex++;
      }
    } else {
      currentIndex++;
    }
  }

  return results;
};

const interpolateRef = <T>(arg: string, resolver: ValueResolver, options: ValueResolverOptions<T>) => {
  const [$ref, property] = arg.split(':');
  return resolver({$ref, property}, {visibilitySet: options.visibilitySet});
};

const interpolateCalcExpression = <T>(
  expr: string,
  resolver: ValueResolver,
  options: ValueResolverOptions<T>,
  keepDecimals = false,
): string => {
  if (!TEMPLATE_CALC_REGEX.test(expr)) return expr;

  // Remove whitespace for easier parsing
  const cleanExpr = expr.replace(/\s+/g, '');

  // Function to resolve a single term
  const resolveTerm = (term: string) => {
    return term.match(/^\d+$/) ? Number(term) : Number(interpolateRef(term, resolver, options) ?? 0);
  };

  // Regex to match terms and operators
  const OPERATOR_REGEX = /([-+*/])/;

  // Split expression into terms and operators
  const tokens = cleanExpr.split(OPERATOR_REGEX).filter(Boolean);

  // Initialize stack for evaluation
  const values: number[] = [];
  const operators: string[] = [];

  // Function to apply operator to top two values in the stack
  const applyOperator = () => {
    const right = values.pop();
    const left = values.pop();
    const operator = operators.pop();

    if (left === undefined || right === undefined || operator === undefined) return;

    switch (operator) {
      case '+':
        values.push(left + right);
        break;
      case '-':
        values.push(left - right);
        break;
      case '*':
        values.push(left * right);
        break;
      case '/':
        values.push(left / right);
        break;
    }
  };

  tokens.forEach(token => {
    if (OPERATOR_REGEX.test(token)) {
      operators.push(token);
    } else {
      values.push(resolveTerm(token));
    }

    // Apply operator if there are enough values and operators
    while (operators.length > 0 && values.length >= 2) {
      applyOperator();
    }
  });

  return keepDecimals ? values[0].toFixed(2) : values[0].toFixed();
};

export function resolveTemplate<T>(
  template: string,
  resolver: ValueResolver,
  options: ValueResolverOptions<T>,
  locale: SupportedLocale,
): string {
  let result = template;

  const expressions = findTemplateExpressions(result);

  // Process expressions from right to left to avoid index shifting
  for (let index = expressions.length - 1; index >= 0; index--) {
    const {expression, start, end} = expressions[index];
    const {func, arg} = TEMPLATE_EXPRESSION_REGEX.exec(expression)?.groups ?? {arg: expression, func: undefined};

    let replacement = '';
    switch (func) {
      case 'date': {
        replacement = normalizeDateInput(interpolateRef(arg, resolver, options))
          .getDate()
          .toFixed();
        break;
      }
      case 'calc': {
        replacement = interpolateCalcExpression(arg, resolver, options);
        break;
      }
      case 'currency': {
        // Try to evaluate as expression first (for computed values like A + B)
        const evaluatedArg = interpolateCalcExpression(arg, resolver, options, true);
        // If it's a valid numeric expression, use it; otherwise try as a simple ref
        const value = Number(evaluatedArg) || Number(interpolateRef(arg, resolver, options) ?? 0);
        replacement = new Intl.NumberFormat(locale + '-CA', {
          style: 'currency',
          currency: 'CAD',
          maximumFractionDigits: 2,
        }).format(value);
        break;
      }
      case 'lower': {
        replacement = (interpolateRef(arg, resolver, options) ?? '').toLowerCase();
        break;
      }
      case 'percentage': {
        const value = interpolateRef(arg, resolver, options) ?? '';
        const normalizedValue = value.replace(',', '.');
        replacement = locale === 'fr' ? `${normalizedValue.replace('.', ',')} %` : `${normalizedValue}%`;
        break;
      }
      default: {
        // Check if expression contains nested templates
        if (findTemplateExpressions(expression).length > 0) {
          // Recursively resolve nested templates first
          const resolvedExpression = resolveTemplate(expression, resolver, options, locale);
          // Also resolve any standalone references in the result
          replacement = interpolateRef(resolvedExpression, resolver, options);
        } else {
          replacement = interpolateRef(expression, resolver, options);
        }
        break;
      }
    }

    result = result.substring(0, start) + replacement + result.substring(end);
  }

  return result;
}
